import { DetailedCoin } from '../types';

/**
 * Base animation durations in milliseconds at 1.0x (normal) speed.
 * Modify these values to adjust standard timings across the board.
 */
export const BASE_ANIMATION_DURATIONS = {
  enterAndFlipMs: 1250,
  exitMs: 350,
  countUpMs: 850,
  inputBufferMs: 250,
};

/**
 * Speed multipliers keyed by rarity tier.
 * Higher number = faster animation (e.g., 2.0 = 2x speed / half duration).
 * Adjust any of these values individually to fine-tune pacing per rarity.
 */
export const RARITY_SPEED_MULTIPLIERS: Record<string, number> = {
  NIFC: 0.7,
  U100k: 0.7,
  U500k: 0.8,
  U1M: 0.9,
  U5M: 1,
  COMMON: 1.3,
};

/**
 * Speed multipliers keyed by denomination for common/U5M coins (>1,000,000 mintage).
 * Denominations with more coins per roll play faster so the overall roll hunt doesn't feel sluggish.
 */
export const DENOMINATION_SPEED_MULTIPLIERS: Record<string, number> = {
  '2euro': 1.0,
  '1euro': 1.05,
  '50cent': 1.1,
  '20cent': 1.15,
  '10cent': 1.2,
  '5cent': 1.25,
  '2cent': 1.3,
  '1cent': 1.35,
};

/**
 * Determines the rarity key for a given coin based on its mintage.
 */
export function getRarityKey(coin?: DetailedCoin | null): string {
  if (!coin) return 'COMMON';
  const status = coin.status || coin.mintageFormatted;
  if (status === 'NIFC' || status === 'Likely NIFC') return 'NIFC';
  if (status === 'CIRC') return 'COMMON';
  if (coin.mintage === 0) return 'NIFC';
  if (coin.mintage <= 100_000) return 'U100k';
  if (coin.mintage <= 500_000) return 'U500k';
  if (coin.mintage <= 1_000_000) return 'U1M';
  if (coin.mintage <= 5_000_000) return 'U5M';
  return 'COMMON';
}

export interface CoinAnimationTimings {
  rarityKey: string;
  multiplier: number;
  shouldFlip: boolean;
  enterAndFlipMs: number;
  exitMs: number;
  countUpMs: number;
  inputBufferMs: number;
}

/**
 * Calculates concrete animation durations in milliseconds for a specific coin.
 * Multiplies speed by denomination multiplier only for COMMON and U5M (>1M mintage).
 * When animation speed is faster than 1.5x (relative to standard 2€ speed 1.0x),
 * coins slide in directly on the national side without flipping.
 */
export function getCoinAnimationTimings(coin?: DetailedCoin | null): CoinAnimationTimings {
  const rarityKey = getRarityKey(coin);
  const rarityMultiplier = RARITY_SPEED_MULTIPLIERS[rarityKey] ?? 1.0;

  // Apply denomination speed scaling ONLY for COMMON and U5M (> 1,000,000 mintage)
  const isCommonOrU5M = rarityKey === 'COMMON' || rarityKey === 'U5M';
  const denom = coin?.denomination || '2euro';
  const denomMultiplier = isCommonOrU5M ? (DENOMINATION_SPEED_MULTIPLIERS[denom] ?? 1.0) : 1.0;

  const totalMultiplier = rarityMultiplier * denomMultiplier;

  // Skip flipping only for >5M mintage (COMMON) 1c, 2c, and 5c coins
  const isSmallDenomCommon =
    rarityKey === 'COMMON' && ['1cent', '2cent', '5cent'].includes(denom);
  const shouldFlip = !isSmallDenomCommon;

  return {
    rarityKey,
    multiplier: totalMultiplier,
    shouldFlip,
    enterAndFlipMs: Math.round(BASE_ANIMATION_DURATIONS.enterAndFlipMs / totalMultiplier),
    exitMs: Math.round(BASE_ANIMATION_DURATIONS.exitMs / totalMultiplier),
    countUpMs: Math.round(BASE_ANIMATION_DURATIONS.countUpMs / totalMultiplier),
    inputBufferMs: Math.round(BASE_ANIMATION_DURATIONS.inputBufferMs / totalMultiplier),
  };
}

export interface RarityVisualEffect {
  glowColor: string;
  auraGradient: string;
  flashColor: string;
  confettiColors: string[];
  particleCount: number;
  hasEffects: boolean;
}

/**
 * Visual effects configuration per rarity tier.
 * Rare tiers (U1M, U500k, U100k, NIFC) have a subtle pulsating radial gradient aura, screen flashes, and confetti bursts.
 */
export const RARITY_VISUAL_EFFECTS: Record<string, RarityVisualEffect> = {
  NIFC: {
    glowColor: '#d97706',
    // Template for optional aura: 'radial-gradient(circle, rgba(217, 119, 6, 0.45) 0%, rgba(251, 191, 36, 0.25) 45%, rgba(217, 119, 6, 0) 70%)'
    auraGradient: 'none',
    flashColor: 'radial-gradient(circle, rgba(251, 191, 36, 0.4) 0%, rgba(217, 119, 6, 0.15) 50%, rgba(0, 0, 0, 0) 75%)',
    confettiColors: ['#d97706', '#fbbf24', '#fde68a', '#ffffff', '#b45309'],
    particleCount: 55,
    hasEffects: true,
  },
  U100k: {
    glowColor: '#dc2626',
    // Template for optional aura: 'radial-gradient(circle, rgba(220, 38, 38, 0.45) 0%, rgba(248, 113, 113, 0.25) 45%, rgba(220, 38, 38, 0) 70%)'
    auraGradient: 'none',
    flashColor: 'radial-gradient(circle, rgba(239, 68, 68, 0.4) 0%, rgba(220, 38, 38, 0.15) 50%, rgba(0, 0, 0, 0) 75%)',
    confettiColors: ['#dc2626', '#f87171', '#fecaca', '#ffffff', '#991b1b'],
    particleCount: 50,
    hasEffects: true,
  },
  U500k: {
    glowColor: '#ea580c',
    // Template for optional aura: 'radial-gradient(circle, rgba(234, 88, 12, 0.4) 0%, rgba(251, 146, 60, 0.22) 45%, rgba(234, 88, 12, 0) 70%)'
    auraGradient: 'none',
    flashColor: 'radial-gradient(circle, rgba(249, 115, 22, 0.35) 0%, rgba(234, 88, 12, 0.12) 50%, rgba(0, 0, 0, 0) 75%)',
    confettiColors: ['#ea580c', '#fb923c', '#fed7aa', '#ffffff', '#c2410c'],
    particleCount: 40,
    hasEffects: true,
  },
  U1M: {
    glowColor: '#7c3aed',
    // Template for optional aura: 'radial-gradient(circle, rgba(124, 58, 237, 0.4) 0%, rgba(167, 139, 250, 0.22) 45%, rgba(124, 58, 237, 0) 70%)'
    auraGradient: 'none',
    flashColor: 'radial-gradient(circle, rgba(139, 92, 246, 0.35) 0%, rgba(124, 58, 237, 0.12) 50%, rgba(0, 0, 0, 0) 75%)',
    confettiColors: ['#7c3aed', '#a78bfa', '#c4b5fd', '#ffffff', '#6d28d9'],
    particleCount: 35,
    hasEffects: true,
  },
  U5M: {
    glowColor: '#16a34a',
    auraGradient: 'none',
    flashColor: 'transparent',
    confettiColors: [],
    particleCount: 0,
    hasEffects: false,
  },
  COMMON: {
    glowColor: 'transparent',
    auraGradient: 'none',
    flashColor: 'transparent',
    confettiColors: [],
    particleCount: 0,
    hasEffects: false,
  },
};

export function getRarityVisualEffect(rarityKey: string): RarityVisualEffect {
  return RARITY_VISUAL_EFFECTS[rarityKey] ?? RARITY_VISUAL_EFFECTS.COMMON;
}

